import {Component, OnInit, signal} from '@angular/core';
import {GeneralHttpService} from "../../http-requests/general-http.service";
import {AuthenticationService} from "../authentication.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {first} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {AlertLevel} from "../../enums/alert-level";
import {UserSelectorService} from "../../_services/user-selector.service";
import {UserJwt} from "../../models/admin_owner/user/user-jwt";
import {UrlHelper} from "../../helpers/url-helper";
import {AppRoutes} from "../../enums/routes";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {NgxOtpInputComponent, NgxOtpInputComponentOptions, NgxOtpStatus} from "ngx-otp-input";
import {SecondFactorChallenge} from "../two-factor/two-factor.models";
import {
  describeSecondFactorError,
  isSecondFactorError,
  readSecondFactorChallenge,
  SecondFactorErrorCodes
} from "../two-factor/second-factor-errors";
import {describeNativeSignInError} from "../local/local-account-errors";
import {OrDividerComponent} from "../or-divider.component";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgxOtpInputComponent,
    OrDividerComponent
  ],
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  /**
   * `ApiError.code` → German UI copy for the refusals the login form can produce.
   *
   * The server's `ApiError.description` is developer-facing English **by design** (it comes from the
   * `ErrorCatalog`, for Postman, logs and docs), so it must never be rendered here — see "Surfacing an API
   * error — branch on `code`, never render `description`" in the frontend style guide.
   *
   * Each sentence has to say what to do next, because both codes below describe a situation the person at the
   * keyboard cannot diagnose from the form: their credentials are correct and the form still refuses them.
   */
  private static readonly errorMessages: Readonly<Record<string, string>> = {
    // Two employees of this company share the address that was typed, so it identifies neither (#279). The
    // remedy is the other door, and it must be spelled out — nobody guesses `username@alias` unprompted.
    E_EMPLOYEE_EMAIL_AMBIGUOUS:
      'Diese E-Mail-Adresse wird von mehreren Mitarbeitern verwendet und ist daher nicht eindeutig. Melden ' +
      'Sie sich stattdessen mit Ihrem Benutzernamen an: benutzername@firmen-kürzel.',
    // The employment ended. Previously this arrived as a 403 and was shown as "Aus Sicherheitsgründen wurde
    // der Zugang temporär deaktiviert" — which is not what happened, and suggests waiting it out.
    E_EMPLOYMENT_ENDED:
      'Für dieses Konto besteht kein aktives Arbeitsverhältnis mehr. Wenden Sie sich an Ihre Administration, ' +
      'wenn das nicht stimmt.'
  };

  /**
   * Generic German refusal for any code {@link errorMessages} does not know yet.
   *
   * Deliberately generic rather than a fallback to `description`: a future backend code that ships without a
   * German mapping must degrade to THIS sentence, never to leaking English. If you land here from a real
   * refusal, add the mapping above — do not make this string more specific.
   */
  private static readonly genericRefusal =
    'Die Anmeldung ist nicht möglich. Bitte versuchen Sie es erneut oder wenden Sie sich an Ihre Administration.';

  /**
   * The German sentence for a structured refusal, or `undefined` when the failure carried no `ApiError` at all
   * (a raw network/5xx failure, or one of the status-only answers the login endpoint gives) — which is a
   * different situation from "we got a code but have not mapped it yet", and one the caller answers itself.
   */
  private static messageFor(code: string | undefined): string | undefined {
    if (code == undefined) return undefined;
    return LoginComponent.errorMessages[code] ?? LoginComponent.genericRefusal;
  }

  /**
   * The mapped sentence for a code, or `undefined` when this map does not know it — unlike
   * {@link messageFor}, which answers {@link genericRefusal} in that case.
   *
   * Both exist because the two vocabularies met here (#279 + #282). An unmapped code must be able to fall
   * through to `describeSecondFactorError`, which owns the second-factor sentences; answering the generic
   * refusal first swallowed every #282 code — including the federated-requirement one, which has its own
   * sentence AND its own remedy, and which a merge briefly reduced to "Die Anmeldung ist nicht möglich".
   * `messageFor` keeps its lenient contract because it is the last resort for a status nothing else handles.
   */
  private static mappedMessageFor(code: string | undefined): string | undefined {
    return code == undefined ? undefined : LoginComponent.errorMessages[code];
  }

  username: string = "";
  password: string = "";
  error: HttpErrorResponse | undefined;
  showLogin: boolean = false;
  protected readonly location = location;

  // ----- the two account kinds, as two forms (#279) -----
  //
  // They resolve DISJOINT populations, and that is the whole reason they are two forms rather than one field the
  // server disambiguates from the shape of the string:
  //
  //  * `native` — an e-mail + password account (a `Local` ExternalIdentity). Never resolves an employee or a
  //    management user.
  //  * `legacy`  — everything that existed before: an employee by e-mail + host alias or
  //    `benutzername@firmen-kürzel`, and a management user by name. Unchanged, including the branch the server
  //    picks and the `E_EMPLOYEE_EMAIL_AMBIGUOUS` refusal that only this form can produce.
  //
  // Native is the default because it is what a new account is. Legacy is one click away and clearly labelled, so
  // somebody can tell which kind of account they hold instead of finding out by which spelling happens to work.

  /** Which credentials form is on screen. */
  protected readonly mode = signal<'native' | 'legacy'>('native');

  /** The native form's fields. Separate from {@link username}/{@link password} so switching cannot carry a
   * half-typed credential from one population's form into the other's request. */
  protected nativeEmail = "";
  protected nativePassword = "";

  /** The native form's inline refusal, already German. */
  protected readonly nativeError = signal<string | null>(null);
  /** A native sign-in is in flight. */
  protected readonly signingIn = signal(false);

  // ----- second step of a sign-in (#282) -----
  //
  // The password step is not a sign-in on its own any more: `POST /auth/User/Login` may answer 403
  // `E_AUTH_TWO_FACTOR_REQUIRED` with a challenge in its error details, and the session only exists after
  // `POST /auth/User/TwoFactor` accepts a code. Modelled as a step on THIS component rather than a separate
  // route because the challenge is short-lived, in-memory and must never be put in a URL or storage —
  // a navigation between the two steps is a place for it to be dropped, bookmarked or shared.

  /** Which step is on screen. `'twoFactor'` is only reachable via a real challenge from the server. */
  protected readonly step = signal<'credentials' | 'twoFactor'>('credentials');
  /**
   * WHICH door raised the challenge, so the code is sent back to the endpoint that can complete it.
   *
   * Both doors can now demand a second factor, and their completions are not interchangeable: one mints an
   * employee's or a management user's session, the other an identity's. The server refuses a challenge sent to
   * the wrong route, so getting this wrong is a refusal rather than a hole — but it is a refusal the person at
   * the keyboard could do nothing about.
   */
  private readonly challengeDoor = signal<'native' | 'legacy'>('legacy');
  /** The live challenge. Held only in memory, only for the life of this step. */
  protected readonly challenge = signal<SecondFactorChallenge | null>(null);
  /** The code step's inline failure, already German. */
  protected readonly twoFactorError = signal<string | null>(null);
  /** A verification round-trip is in flight — blocks a double submit of the same (single-use) code. */
  protected readonly verifying = signal(false);
  /** Recovery-code mode: a free-text code instead of the 6-digit app code. */
  protected readonly usingRecoveryCode = signal(false);
  /** The typed recovery code. Separate from the OTP control, which only accepts six digits. */
  protected readonly recoveryCode = signal('');
  /** Drives the OTP control's rejected styling without this component owning its internals. */
  protected readonly otpStatus = signal<NgxOtpStatus | undefined>(undefined);

  /**
   * Options for the shared `ngx-otp-input`. German `ariaLabels` because the control's defaults are English and
   * this app has no i18n layer; `inputMode: 'numeric'` so a phone offers a number pad for a 6-digit code.
   */
  protected readonly otpOptions: NgxOtpInputComponentOptions = {
    otpLength: 6,
    autoFocus: true,
    inputMode: 'numeric',
    regexp: /^[0-9]+$/,
    ariaLabels: [
      'Ziffer 1 von 6', 'Ziffer 2 von 6', 'Ziffer 3 von 6',
      'Ziffer 4 von 6', 'Ziffer 5 von 6', 'Ziffer 6 von 6'
    ]
  };

  /** Dev-only external login (Development + CHEQME_DEV_AUTH). Hidden in every normal/production deployment. */
  protected devAuthEnabled = false;
  protected devEmail = "";

  constructor(public AuthenticationService: AuthenticationService,
              private router: Router,
              private generalHttpService: GeneralHttpService,
              private route: ActivatedRoute,
              private globalAlertService: GlobalAlertService,
              public UserSelectorService: UserSelectorService) {
    if (this.AuthenticationService.userValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    this.AuthenticationService.devLoginAvailable().pipe(first())
      .subscribe(enabled => this.devAuthEnabled = enabled);
  }

  /** The shared "signing in" landing page every provider redirects back to (handoff exchange + routing home). */
  private get signingInReturnUrl(): string {
    return `${location.origin}/${AppRoutes.AuthenticationSigningIn}`;
  }

  /**
   * Starts a DIRECT Google sign-in (#279) — `/auth/google/login`, straight against Google, no Entra broker.
   */
  loginWithGoogle() {
    this.AuthenticationService.googleLogin(this.signingInReturnUrl);
  }

  /**
   * Starts the ONE Microsoft sign-in — `/auth/microsoft/login`, against the app's own `/common` app
   * registration. There is no separate platform door any more: the backend grants `PlatformOwner` on this same
   * route when the token's `tid` matches `CHEQME_MICROSOFT_PLATFORM_TENANT_ID`, so a platform owner and any
   * other Microsoft user press the identical button.
   */
  loginWithMicrosoft() {
    this.AuthenticationService.microsoftLogin(this.signingInReturnUrl);
  }

  /**
   * Starts a DIRECT Apple sign-in (#279) — `/auth/apple/login`, against `https://appleid.apple.com`, no Entra
   * broker. Mirrors {@link loginWithGoogle}/{@link loginWithMicrosoft}; no consumer sign-in is brokered any more.
   *
   * **404s until `CHEQME_APPLE_*` is configured**, which is the deliberate choice: an unconfigured provider
   * should look unconfigured rather than silently falling back to Entra, which is how the broker went on serving
   * Apple unnoticed after the other two had moved off it.
   */
  loginWithApple() {
    this.AuthenticationService.appleLogin(this.signingInReturnUrl);
  }

  /** DEV-ONLY: sign in as a dummy external identity (the email need not exist) — same handoff as Microsoft. */
  loginWithDev() {
    if (!this.devEmail.trim()) return;
    this.AuthenticationService.devLogin(this.devEmail.trim());
  }

  /**
   * The primary sign-in: an e-mail address and a password, against native accounts only.
   *
   * Its session is the same shape a federated sign-in produces — contextless — so it lands on the signing-in
   * page rather than at {@link landAfterSignIn}. That page owns the one decision about where such a session
   * goes (enter its single context silently, or ask which of several), and a second copy of it here would be
   * the copy that fell behind.
   */
  loginWithPassword() {
    if (this.signingIn()) return;
    this.signingIn.set(true);
    this.nativeError.set(null);

    this.AuthenticationService.loginWithPassword(this.nativeEmail, this.nativePassword).pipe(first()).subscribe({
      next: () => window.open(`/${AppRoutes.AuthenticationSigningIn}`, '_self'),
      error: (error: HttpErrorResponse) => {
        this.signingIn.set(false);

        // An enrolled second factor is demanded before a session exists — the same rule the legacy door applies,
        // arriving as the same code with the same challenge in the error details, so ONE screen serves both.
        const challenge = readSecondFactorChallenge(error);
        if (challenge) {
          this.challengeDoor.set('native');
          this.enterTwoFactorStep(challenge);
          return;
        }

        // Every other failure gets the same sentence AND the same legacy hint. The server answers one code
        // whether the address is unknown, belongs to a federated account, or merely had the wrong password — a
        // hint shown only for addresses that exist would leak exactly what that single code exists to hide. See
        // LEGACY_LOGIN_HINT for how the hint is retired when the legacy login goes (#264).
        this.nativeError.set(describeNativeSignInError(error?.error?.code));
      }
    });
  }

  /** Switches between the two forms, clearing whatever the other one was showing. */
  protected useMode(mode: 'native' | 'legacy') {
    this.mode.set(mode);
    this.nativeError.set(null);
    // The passwords are dropped, not carried across: the two forms address different account populations, and a
    // credential typed for one has no business being submitted to the other.
    this.password = "";
    this.nativePassword = "";
  }

  login(name: string, password: string) {
    // The challenge this may raise belongs to the form that raised it, and only the legacy form can: the native
    // door has no second step, because the company's requirement is enforced when a context is entered rather
    // than at sign-in. Stamping the mode here is what makes `backToCredentials` return to the fields the person
    // was actually using instead of to whichever form happened to be showing.
    this.mode.set('legacy');
    this.challengeDoor.set('legacy');

    this.AuthenticationService.login(name, password).pipe(first()).subscribe({
      next: () => this.landAfterSignIn(),
      error: (error: HttpErrorResponse) => {
        // #282 FIRST, ahead of every other arm: `E_AUTH_TWO_FACTOR_REQUIRED` is a 403, so the status-based
        // wording below would otherwise swallow it — telling somebody their account was suspended when it is
        // simply asking for their app code.
        const challenge = readSecondFactorChallenge(error);
        if (challenge) {
          this.enterTwoFactorStep(challenge);
          return;
        }

        // A structured refusal wins over the status-only wording: it is the only one that can say WHY correct
        // credentials were refused. Before this, anything that was neither 401 nor 403 — the ambiguous-address
        // 409 among them — produced no message at all, so the form simply appeared to do nothing.
        //
        // The 403 arm goes through `describeSecondFactorError` rather than the bare historical sentence,
        // because a #282 code that is NOT a challenge (today: the company requires a federated sign-in this
        // account cannot give) still deserves its own wording rather than "temporär deaktiviert".
        // The MAPPED lookup, not the lenient one: an unmapped code has to reach the 403 arm below, where
        // `describeSecondFactorError` knows the #282 vocabulary. The lenient `messageFor` stays as the last
        // resort for a status neither arm handles (a 409/500/0 that carried a code nobody has translated).
        const mapped = LoginComponent.mappedMessageFor(error.error?.code);
        const message = mapped
          ?? (error.status == 401 ? "Bitte Benutzerdaten überprüfen."
            : error.status == 403 ? describeSecondFactorError(error,
              "Aus Sicherheitsgründen wurde der Zugang temporär deaktiviert.")
              : LoginComponent.messageFor(error.error?.code) ?? LoginComponent.genericRefusal);

        // 8s rather than 3s for a mapped sentence: those name a different login to use, which nobody can read,
        // absorb and act on in three seconds. A 403 keeps #282's 5s — its sentences are longer than the
        // historical one but shorter than "sign in the other way instead".
        this.globalAlertService.createAlertBannerModel("Fehler", message, AlertLevel.error,
          mapped ? 8000 : error.status == 403 ? 5000 : 3000);
        this.globalAlertService.show();
      }
    })

    //this.generalHttpService.login(name, password).subscribe(x => {
    //  CookieHelper.setCookie("JWT", x.jwt, 365);
    //  window.location.reload();
    //}, error => console.error(error));
  }

  /**
   * Answers the challenge with a code from the authenticator app (or a recovery code) — the second half of a
   * sign-in. Success establishes the session and lands exactly where a one-step sign-in would.
   */
  verifySecondFactor(code: string) {
    const challenge = this.challenge();
    const trimmed = (code ?? '').trim();
    // Guarded rather than merely disabled in the template: the OTP control fires `otpComplete` on every
    // keystroke that completes the six digits, so a correction after a rejected code can re-fire it while the
    // previous request is still open — and these codes are single-use, so a duplicate submit burns the code the
    // person is about to be told was wrong.
    if (!challenge || trimmed.length === 0 || this.verifying()) return;

    this.verifying.set(true);
    this.twoFactorError.set(null);
    this.otpStatus.set(undefined);

    // Routed by the door that raised the challenge: a native session is contextless and lands on the signing-in
    // page, which owns the decision about where such a session goes; a legacy one already has its tenant and
    // lands as a one-step sign-in would.
    const native = this.challengeDoor() === 'native';
    const complete = native
      ? this.AuthenticationService.completeLocalTwoFactor(challenge.challenge, trimmed)
      : this.AuthenticationService.completeTwoFactorLogin(challenge.challenge, trimmed);

    complete.pipe(first()).subscribe({
      next: () => native
        ? window.open(`/${AppRoutes.AuthenticationSigningIn}`, '_self')
        : this.landAfterSignIn(),
      error: (error: HttpErrorResponse) => {
        this.verifying.set(false);

        // An expired/mismatched challenge cannot be retried — every further code would be refused for a reason
        // that has nothing to do with the code. Return to the password step and say so there, rather than
        // leaving somebody typing fresh codes into a dead challenge.
        if (isSecondFactorError(error, SecondFactorErrorCodes.ChallengeInvalid)) {
          this.backToCredentials();
          this.globalAlertService.createAlertBannerModel("Fehler",
            describeSecondFactorError(error), AlertLevel.error, 5000);
          this.globalAlertService.show();
          return;
        }

        this.otpStatus.set(NgxOtpStatus.FAILED);
        // A lock-out refuses app codes but still ACCEPTS a recovery code, and clears the lock with it — so
        // during a lockout that route is the only one that works and the message has to point at it. Only
        // where the account actually has codes, and not for somebody already typing one: a `Locked` answer to
        // a recovery code means that code did not match, so "use a recovery code" is advice they are already
        // following. Nothing here disables the route — see the template, where the toggle and the recovery
        // submit are deliberately not gated on the lock.
        this.twoFactorError.set(describeSecondFactorError(error,
          "Der Code konnte nicht geprüft werden. Bitte versuchen Sie es erneut.", {
            recoveryCodeAvailable: this.challenge()?.recoveryCodesAvailable === true
              && !this.usingRecoveryCode()
          }));
      }
    });
  }

  /** Switches between the 6-digit app code and a recovery code. Only offered when the server said there are any. */
  toggleRecoveryCode(useRecovery: boolean) {
    this.usingRecoveryCode.set(useRecovery);
    this.recoveryCode.set('');
    this.twoFactorError.set(null);
    this.otpStatus.set(undefined);
  }

  /** Abandons the code step and returns to username/password, dropping the (now useless) challenge. */
  backToCredentials() {
    this.step.set('credentials');
    this.challenge.set(null);
    this.usingRecoveryCode.set(false);
    this.recoveryCode.set('');
    this.twoFactorError.set(null);
    this.otpStatus.set(undefined);
    this.verifying.set(false);
    this.password = "";
  }

  /** Moves to the code step. The password is dropped here — it has done its job and nothing else needs it. */
  private enterTwoFactorStep(challenge: SecondFactorChallenge) {
    this.challenge.set(challenge);
    this.usingRecoveryCode.set(false);
    this.recoveryCode.set('');
    this.twoFactorError.set(null);
    this.otpStatus.set(undefined);
    this.verifying.set(false);
    this.password = "";
    this.step.set('twoFactor');
  }

  /** Where a completed sign-in goes — shared by both steps so they cannot land differently. */
  private landAfterSignIn() {
    const returnUrl = UrlHelper.QueryParams.returnUrl || '/';
    // const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    window.open(returnUrl, '_self');
  }

  reauth(user: UserJwt) {
    this.username = user.name;
    this.showLogin = true;
    // A remembered account is re-authenticated on the form its kind belongs to. `authSource` is minted from the
    // provider that established the session, and a native account carries 'Local' exactly as a legacy password
    // login does — so the remembered NAME is what separates them: a native account's is its e-mail address,
    // which is also its username. Landing somebody on the wrong form would refuse a correct password.
    if (user.authSource === 'Local' && user.name?.includes('@') && !user.company?.id) {
      this.nativeEmail = user.name;
      this.mode.set('native');
      return;
    }
    this.mode.set('legacy');
  }
}
